"use client";

import { siteHref } from "../current";
import { useRouter } from "../navigation";
import { useState } from "react";
import { deletePost, savePost } from "../actions";
import type { MediaItem, PostRecord } from "../types";
import { BlockEditor, fromEditor, toEditor } from "./BlockEditor";
import { LanguageBar, type EditLang } from "./locale";
import { ImageField } from "./media";
import { SaveBar, useSave } from "./SaveBar";
import { CARD, ConfirmButton, PageTitle, Select, StatusSwitch, TextArea, TextField, today } from "./ui";

const CATEGORIES = ["Branding", "Marketing", "Digital", "Technology", "News"];
const slugify = (t: string) =>
  t.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

/** Create or edit a blog post, in English and Arabic. The Arabic version shows on /ar/blog once it has a title,
 * the short answer and some text. */
export function PostEditor({ post, media }: { post: PostRecord | null; media: MediaItem[] }) {
  const router = useRouter();
  const isNew = !post;
  const [lang, setLang] = useState<EditLang>("en");
  const [form, setForm] = useState(() => ({
    id: post?.id ?? "",
    status: post?.status ?? ("draft" as const),
    title: post?.title ?? "",
    slug: post?.slug ?? "",
    summary: post?.summary ?? "",
    description: post?.description ?? "",
    category: post?.category ?? "Branding",
    author: post?.author ?? "TASMA team",
    published: post?.published ?? today(),
    updated: post?.updated ?? today(),
    coverImage: post?.coverImage ?? null,
    seoTitle: post?.seoTitle ?? "",
  }));
  const [ar, setAr] = useState(() => ({
    title: post?.ar?.title ?? "",
    summary: post?.ar?.summary ?? "",
    description: post?.ar?.description ?? "",
    author: post?.ar?.author ?? "فريق TASMA",
    seoTitle: post?.ar?.seoTitle ?? "",
    coverAlt: post?.ar?.coverAlt ?? "",
  }));
  const [blocks, setBlocks] = useState(() => toEditor(post?.body ?? []));
  const [arBlocks, setArBlocks] = useState(() => toEditor(post?.ar?.body ?? []));
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const { pending, notice, dirty, markDirty, run } = useSave();

  const set = <K extends keyof typeof form>(field: K, value: (typeof form)[K]) => {
    markDirty();
    setForm((f) => ({ ...f, [field]: value, ...(field === "title" && !slugTouched ? { slug: slugify(String(value)) } : {}) }));
  };
  const setArField = <K extends keyof typeof ar>(field: K, value: (typeof ar)[K]) => {
    markDirty();
    setAr((a) => ({ ...a, [field]: value }));
  };
  const hasArabic = Boolean(ar.title.trim() && ar.summary.trim() && fromEditor(arBlocks).length);

  const save = () =>
    run(
      () => savePost({ ...form, body: fromEditor(blocks), seoTitle: form.seoTitle || undefined, ar: { ...ar, body: fromEditor(arBlocks) } }),
      form.status === "published"
        ? hasArabic
          ? "Saved and published in English and Arabic. It's live on the website."
          : "Saved and published in English. Add the Arabic version to show it on the Arabic site too."
        : "Saved as a draft (not on the website).",
      (result) => {
        if (result.ok && isNew && result.id) router.replace(`/admin/blog/${result.id}`);
      },
    );

  return (
    <div>
      <PageTitle title={isNew ? "New blog post" : "Edit blog post"}>
        {!isNew && form.status === "published" && (
          <>
            <a href={siteHref(`/blog/${form.slug}`)} target="_blank" rel="noreferrer" className="py-2 text-sm text-teal underline underline-offset-4">
              View on website ↗
            </a>
            {hasArabic && (
              <a href={siteHref(`/ar/blog/${form.slug}`)} target="_blank" rel="noreferrer" className="py-2 text-sm text-teal underline underline-offset-4">
                Arabic ↗
              </a>
            )}
          </>
        )}
      </PageTitle>

      <LanguageBar
        lang={lang}
        onChange={setLang}
        missing={{ ar: !hasArabic }}
        arabicNote="The Arabic article for /ar/blog. It appears there once it has a title, the short answer and some text. Web address, category, dates, cover image and status are shared: change them on the English tab."
      />

      {lang === "en" ? (
        <div className="flex flex-col gap-6">
          <section className={`${CARD} flex flex-col gap-5`}>
            <TextField label="Title" required value={form.title} onChange={(v) => set("title", v)} max={110} placeholder="e.g. How to Choose a Branding Agency in Egypt" />
            <TextField
              label="Web address"
              required
              value={form.slug}
              onChange={(v) => {
                setSlugTouched(true);
                set("slug", slugify(v));
              }}
              hint={<>The article&apos;s address: <strong>/blog/{form.slug || "…"}</strong> (Arabic: <strong>/ar/blog/{form.slug || "…"}</strong>).{!isNew && " Changing it breaks links people already shared."}</>}
            />
            <TextArea label="The short answer" required value={form.summary} onChange={(v) => set("summary", v)} rows={4} hint="40–60 words shown first on the article. Answer the title's question directly." />
            <BlockEditor
              value={blocks}
              onChange={(b) => {
                markDirty();
                setBlocks(b);
              }}
              media={media}
            />
          </section>

          <section className={`${CARD} flex flex-col gap-5`}>
            <h2 className="text-xl font-bold">Details</h2>
            <TextArea label="Card & search description" required value={form.description} onChange={(v) => set("description", v)} max={160} rows={3} hint="120–160 characters. Shown on the blog cards and under the result in Google." />
            <div className="grid gap-5 sm:grid-cols-2">
              <Select label="Category" value={form.category} onChange={(v) => set("category", v)} options={CATEGORIES.map((c) => ({ value: c, label: c }))} />
              <TextField label="Author" value={form.author} onChange={(v) => set("author", v)} />
              <TextField label="Published" type="date" value={form.published} onChange={(v) => set("published", v)} />
              <TextField label="Last updated" type="date" value={form.updated} onChange={(v) => set("updated", v)} hint="Change it when the article changes meaningfully." />
            </div>
            <ImageField label="Cover image" value={form.coverImage} onChange={(v) => set("coverImage", v)} media={media} hint="Shown on the article's card on the Blog page and at the top of the article (both languages). Landscape works best (about 16:10); keep the subject near the centre, phones crop the sides a little. Without one, The Watching A stands in." />
            <TextField label="Title in Google (optional)" value={form.seoTitle} onChange={(v) => set("seoTitle", v)} max={60} hint="Leave empty to use the title." />
          </section>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <section className={`${CARD} flex flex-col gap-5`}>
            <TextField label="Title" lang="ar" value={ar.title} onChange={(v) => setArField("title", v)} max={110} placeholder={form.title} />
            <TextArea label="The short answer" lang="ar" value={ar.summary} onChange={(v) => setArField("summary", v)} rows={4} hint="40–60 words shown first on the article." />
            <BlockEditor
              lang="ar"
              value={arBlocks}
              onChange={(b) => {
                markDirty();
                setArBlocks(b);
              }}
              media={media}
            />
          </section>

          <section className={`${CARD} flex flex-col gap-5`}>
            <h2 className="text-xl font-bold">Details</h2>
            <TextArea label="Card & search description" lang="ar" value={ar.description} onChange={(v) => setArField("description", v)} max={160} rows={3} hint="120–160 characters, for the Arabic blog cards and Google." />
            <TextField label="Author" lang="ar" value={ar.author} onChange={(v) => setArField("author", v)} />
            {form.coverImage && <TextField label="Cover image description (alt text)" lang="ar" value={ar.coverAlt} onChange={(v) => setArField("coverAlt", v)} placeholder={form.coverImage.alt} hint="Describes the cover image for people who can't see it. Leave empty to use the English one." />}
            <TextField label="Title in Google (optional)" lang="ar" value={ar.seoTitle} onChange={(v) => setArField("seoTitle", v)} max={60} hint="Leave empty to use the title." />
          </section>
        </div>
      )}

      <SaveBar onSave={save} pending={pending} dirty={dirty} notice={notice}>
        <StatusSwitch value={form.status} onChange={(v) => set("status", v)} />
        {!isNew && (
          <ConfirmButton
            question="Delete this post (both languages)?"
            onConfirm={() =>
              run(() => deletePost(form.id), "Deleted.", (r) => {
                if (r.ok) router.replace("/admin/blog");
                  router.refresh(); // show the list without the deleted item
              })
            }
          />
        )}
      </SaveBar>
    </div>
  );
}
